import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import styled, { css } from "styled-components";
import "react-phone-input-2/lib/style.css";
import parsePhoneNumberFromString from "libphonenumber-js";

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.span`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
`;

const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
`;

const StyledPhoneInputWrapper = styled.div`
  .react-tel-input {
    width: 100%;
    display: flex;
    align-items: center;
    border: 1px solid
      ${(props) => (props.error ? "rgb(220, 38, 38)" : "rgb(224, 228, 244)")};
    border-radius: 8px;
    padding: 0 12px;
    background: #ffffff;
    height: 42px;
    transition: border-color 0.2s;

    &:focus-within {
      border-color: rgb(97, 98, 197);
    }

    input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 16px;
      font-family: Montserrat, sans-serif;
      font-weight: 500;
      color: rgb(33, 37, 67);
      background: transparent;
      padding-left: 48px; // enough space for the flag
    }

    .flag-dropdown {
      border: none;
      background: transparent;
    }

    .selected-flag {
      padding-left: 0;
    }
  }
`;

const isValidUKPhoneNumber = (formatted) => {
  const number = formatted.replace("+", "");
  if (!number.startsWith("44")) return false;

  const national = number.slice(2); // strip +44
  const prefix2 = national.slice(0, 2);
  const prefix3 = national.slice(0, 3);
  const prefix4 = national.slice(0, 4);
  const length = national.length;

  // Mobile: 071–075, 077–079
  if ((/^7[1-5]$/.test(prefix2) || /^7[7-9]$/.test(prefix2)) && length === 10) {
    return true;
  }

  // Freephone
  if (prefix3 === "800" && (length === 9 || length === 10)) return true;
  if (prefix3 === "808" && length === 10) return true;

  // Geographic
  if (national.startsWith("1") && (length === 9 || length === 10)) return true;
  if (national.startsWith("2") && length === 10) return true;

  // Non-geographic (084x, 087x, 03xx)
  if (/^84[345]|^87[012]$/.test(prefix4) && length === 10) return true;
  if (/^03[0347]$/.test(prefix4) && length === 10) return true;

  return false;
};

const PhoneInputField = ({
  label,
  value,
  onChange,
  required = false,
  error = false,
  errorMessage = "",
  ...props
}) => {
  const [raw, setRaw] = useState(value || "");

  const handlePhoneChange = (val, countryData) => {
    setRaw(val);

    const countryCode = countryData?.countryCode?.toUpperCase(); // e.g. 'GB'
    const phoneNumber = parsePhoneNumberFromString(val, countryCode);

    if (phoneNumber && phoneNumber.isValid()) {
      const formatted = phoneNumber.number; 

      // Only apply UK-specific rules for UK numbers
      if (countryCode === "GB") {
        if (isValidUKPhoneNumber(formatted)) {
          onChange?.(formatted);
        } else {
          console.warn("Invalid UK-specific phone number format");
          // Optionally: set a validation error here
        }
      } else {
        // For all other countries, use general libphonenumber validation
        onChange?.(formatted);
      }
    } else {
      console.warn("Invalid phone number format");
      // Optionally: handle error here
    }
  };

  return (
    <InputContainer>
      {label && (
        <Label>
          {label}
          {required && <Required>*</Required>}
        </Label>
      )}
      <StyledPhoneInputWrapper error={error}>
        <PhoneInput
          country="gb"
          value={raw}
          onChange={handlePhoneChange}
          enableSearch={true}
          countryCodeEditable={false}
          inputProps={{
            required: true,
            name: "phone",
            autoFocus: true,
          }}
          {...props}
        />
      </StyledPhoneInputWrapper>
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </InputContainer>
  );
};

export default PhoneInputField;
